import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import _ from 'lodash';
import ScannedCell from '../ScannedCell';
import { SamplePosition } from '../../../../../../../../core/types/common.types';
import ScanEvent from '../../../../../../../../components/forms/ScanEvent';
import EditSampleDialog from '../EditSampleDialog';
import { FormikErrors } from 'formik';
import { STORAGE_BOX_WIDTH } from '../../../../../../../../core/constants/forms.constants';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '../../../../../../../../store';
import { getParticipantSampleByBarcodeThunk } from '../../../../../../../../store/storage/thunks';
import { participantSampleByBarcodeResponseSelector } from '../../../../../../../../store/storage/selectors';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { theme } from '@omnigenbiodata/ui';

interface CryoboxGridProps {
  errors?: string | string[] | FormikErrors<SamplePosition>[];
  replace: (index: number, value: any) => void;
  onSelect: (index: number | undefined) => void;
  values: SamplePosition[];
  selectedSample?: number;
  canScan: boolean;
  editMode: boolean;
  sequencer?: string;
}

function SampleGrid({
  errors,
  replace,
  onSelect,
  selectedSample,
  values,
  canScan,
  editMode,
  sequencer,
}: CryoboxGridProps) {
  const firstOpen = useMemo(() => values.findIndex((item) => !item), [values]);
  const [currentSampleBarcode, setCurrentSampleBarcode] = useState<string>('');
  const [currentSelectedSample, setCurrentSelectedSample] = useState<number | undefined>(undefined);
  const [scannedSampleSequencer, setScannedSampleSequencer] = useState('');
  const [msgBoxOpen, setMsgBoxOpen] = useState(false);
  const rows = _.chunk(values, STORAGE_BOX_WIDTH);
  const dispatch = useAppDispatch();
  const participantSampleByBarcodeResponse = useAppSelector(participantSampleByBarcodeResponseSelector);

  const handleScan = (scanCode: string) => {
    setCurrentSampleBarcode(scanCode);
    setCurrentSelectedSample(selectedSample);
    dispatch(getParticipantSampleByBarcodeThunk(scanCode));
  };

  const deleteSample = (index: number) => {
    replace(index, null);
    onSelect(undefined);
  };

  const handleSampleSequencerCheck = () => {
    let index;

    if (participantSampleByBarcodeResponse?.sequencer !== sequencer && sequencer !== 'MIXED') {
      if (participantSampleByBarcodeResponse) {
        setScannedSampleSequencer(participantSampleByBarcodeResponse.sequencer);
        setMsgBoxOpen(true);
      }
    } else {
      if (currentSelectedSample !== undefined) {
        index = currentSelectedSample;
      } else {
        index = firstOpen;
      }

      if (index > -1) {
        replace(index, {
          sampleBarcode: currentSampleBarcode,
          sequencer: participantSampleByBarcodeResponse?.sequencer,
          samplePosition: index,
        });
        setCurrentSelectedSample(undefined);
      }
    }
  };

  useEffect(() => {
    if (participantSampleByBarcodeResponse) {
      handleSampleSequencerCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantSampleByBarcodeResponse]);

  return (
    <Box mb={4} position="relative">
      {canScan && selectedSample === undefined && <ScanEvent data-testid="scanner" onScan={handleScan} />}
      {selectedSample !== undefined && (
        <EditSampleDialog
          index={selectedSample}
          value={values[selectedSample]}
          isOpen={selectedSample !== undefined ? true : false}
          onClose={() => {
            onSelect(undefined);
          }}
          onSave={handleScan}
          onRemove={deleteSample}
          sequencer={sequencer}
        />
      )}
      <Paper elevation={5} sx={{ padding: 1 }}>
        {sequencer && (
          <Typography variant="body1" align="center" mb={2} mt={2}>
            <strong>Sequencer:</strong> {sequencer}
          </Typography>
        )}
        {rows.map((row, rowIndex) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
            key={`col-${rowIndex}`}
          >
            {row.map((col, colIndex) => {
              const shelfPosition = String.fromCharCode(rowIndex + 65) + (colIndex + 1);
              const cellIndex = Math.ceil(colIndex) + Math.ceil(rowIndex * STORAGE_BOX_WIDTH);
              return (
                <ScannedCell
                  key={cellIndex}
                  value={col}
                  position={cellIndex}
                  label={shelfPosition}
                  onSelect={onSelect}
                  onRemove={deleteSample}
                  hasError={errors && errors[cellIndex]}
                  editMode={editMode}
                  sequencer={sequencer}
                />
              );
            })}
          </Box>
        ))}
      </Paper>
      <Dialog
        open={msgBoxOpen}
        onClose={() => {
          setMsgBoxOpen(false);
        }}
        aria-labelledby="alert-dialog-finish"
        maxWidth="xs"
      >
        <DialogContent>
          <Typography variant="h5">Sequencer Mismatch</Typography>
          <br />
          <Typography variant="body1">
            You are trying to add a {scannedSampleSequencer || ''} sample to a {sequencer || ''} box. If you want to add
            both types of samples to this box please reassign it as a MIXED sequencer box.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="small"
            sx={{
              fontSize: '18px',
              borderColor: theme.palette.primary.light,
            }}
            onClick={() => {
              setMsgBoxOpen(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SampleGrid;
